/**
 * useTaskActivityLog — Histórico de auditoria de uma tarefa
 *
 * Lê eventos gravados em `task_activity_log` (created/updated/deleted/
 * phase_changed/status_changed/assigned/...) via RPC `get_task_activity_log`.
 *
 * RELEASE: 2026-06-11 v1.0.0 — Criação inicial.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useTaskActivityLog');

/** Tipos de ação suportados (alinhado com a trigger no banco). */
export type TaskActivityAction =
  | 'created'
  | 'updated'
  | 'deleted'
  | 'phase_changed'
  | 'status_changed'
  | 'assigned'
  | 'unassigned'
  | 'priority_changed'
  | 'due_date_changed'
  | 'category_changed'
  | 'classification_changed';

/** Item da timeline retornado pela RPC. */
export interface TaskActivityLogEntry {
  id: string;
  task_id: string;
  task_number: number | null;
  task_title: string | null;
  action: TaskActivityAction;
  performed_by: string | null;
  performer_name: string | null;
  performer_avatar: string | null;
  changes: Record<string, { from?: unknown; to?: unknown; changed?: boolean }>;
  metadata: Record<string, unknown>;
  /** Dicionário UUID → nome (Categoria/Fase/Classificação/Usuário) — v1.1.0. */
  labels?: Record<string, string>;
  created_at: string;
}

export function useTaskActivityLog(taskId: string | null, options?: { enabled?: boolean }) {
  const { enabled = true } = options || {};
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const query = useQuery({
    queryKey: ['task-activity-log', taskId, tenantId],
    queryFn: async (): Promise<TaskActivityLogEntry[]> => {
      if (!taskId) return [];

      const { data, error } = await supabase.rpc('get_task_activity_log', {
        p_task_id: taskId,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Falha ao carregar histórico da tarefa', error.message);
        throw error;
      }

      return (data || []) as TaskActivityLogEntry[];
    },
    enabled: enabled && !!taskId && !!tenantId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  return {
    logs: query.data || [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
